import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Ruler, PenTool, Factory, Wrench, Truck, Shield, Clock, Award, Check, ArrowRight, Download } from 'lucide-react';
import Footer from '../../components/Footer';
import { fadeInUp, scaleIn } from '../../hooks/useScrollAnimation';

const Capabilities = () => {
  const capabilities = [
    { 
      icon: Ruler, 
      title: 'Site Survey',
      description: 'Our experts visit your facility to measure, assess, and understand your specific storage requirements and constraints.',
      features: ['On-site measurement', 'Load assessment', 'Space optimization analysis']
    },
    { 
      icon: PenTool, 
      title: 'CAD Layout',
      description: 'Custom 3D CAD designs and layouts that maximize your space utilization and workflow efficiency.',
      features: ['3D visualization', 'Load calculations', 'Building control drawings']
    },
    { 
      icon: Factory, 
      title: 'Manufacture',
      description: 'Quality-controlled fabrication with certified materials.',
      features: ['SABS certified materials', 'Quality control', 'Local production']
    },
    { 
      icon: Wrench, 
      title: 'Installation',
      description: 'Professional installation by certified technicians ensuring safety, compliance, and optimal performance.',
      features: ['Certified installers', 'Safety compliant', 'Quality assurance']
    },
  ];

  const features = [
    { icon: Truck, title: 'Nationwide Delivery', desc: 'Across South Africa & SADC region' },
    { icon: Shield, title: 'SABS Compliant', desc: 'Certified materials & safety standards' },
    { icon: Clock, title: 'On-Time Delivery', desc: 'We meet your project deadlines' },
    { icon: Award, title: 'B-BBEE Level 1', desc: 'Government tender ready' },
  ];

  const processSteps = [
    { step: '01', title: 'Consultation', desc: 'We discuss your needs, budget, and timeline' },
    { step: '02', title: 'Site Survey', desc: 'Our team visits to measure and assess' },
    { step: '03', title: 'Design', desc: 'Custom CAD layouts and 3D visualizations' },
    { step: '04', title: 'Quotation', desc: 'Detailed proposal with transparent pricing' },
    { step: '05', title: 'Manufacturing', desc: 'Local production with quality materials' },
    { step: '06', title: 'Installation', desc: 'Professional setup by certified technicians' },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      }
    }
  };

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="w-full bg-charcoal py-16 lg:py-24 overflow-hidden">
        <div className="px-6 lg:px-[7vw]">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Link to="/" className="text-white/60 hover:text-white text-sm mb-4 inline-block">
              ← Back to Home
            </Link>
          </motion.div>
          <motion.h1 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="font-display font-bold text-white mb-4"
            style={{ fontSize: 'clamp(32px, 5vw, 64px)', lineHeight: 1.1 }}
          >
            SURVEY. DESIGN. BUILD. INSTALL.
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-white/60 max-w-2xl text-lg"
          >
            End-to-end industrial storage solutions for warehouses, retail, and manufacturing facilities across South Africa.
          </motion.p>
        </div>
      </section>

      {/* Main Capabilities */}
      <section className="w-full bg-offwhite py-12 lg:py-20">
        <div className="px-6 lg:px-[7vw]">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="font-display font-bold text-charcoal text-2xl lg:text-3xl mb-3">
              Our Capabilities
            </h2>
            <p className="text-charcoal/60 max-w-2xl mx-auto">
              From first measurement to final bolt, we handle the full lifecycle—so your team can stay focused on operations.
            </p>
          </motion.div>

          <motion.div 
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            className="grid grid-cols-2 lg:grid-cols-2 gap-6 mb-16"
          >
            {capabilities.map((cap, index) => (
              <motion.div
                key={index}
                variants={scaleIn}
                whileHover={{ y: -8, transition: { duration: 0.3 } }}
                className="bg-white rounded-2xl p-6 lg:p-8 shadow-sm"
              >
                <motion.div 
                  className="w-14 h-14 rounded-xl bg-lime/100 flex items-center justify-center mb-5"
                  whileHover={{ scale: 1.1, rotate: 5 }}
                  transition={{ duration: 0.3 }}
                >
                  <cap.icon className="w-7 h-7 text-charcoal" />
                </motion.div>
                <h3 className="font-display font-bold text-charcoal text-xl mb-3">
                  {cap.title}
                </h3>
                <p className="text-charcoal/70 mb-5 leading-relaxed text-sm">
                  {cap.description}
                </p>
                <ul className="space-y-2">
                  {cap.features.map((feature, fIndex) => (
                    <li key={fIndex} className="flex items-center gap-2 text-sm text-charcoal/60">
                      <Check className="w-8 h-8 text-lime/100 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </motion.div>

          {/* Features Grid */}
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="bg-white rounded-2xl p-6 lg:p-10 shadow-sm mb-12"
          >
            <h3 className="font-display font-bold text-charcoal text-xl mb-6 text-center">
              Why Choose Molomo NR
            </h3>
            <motion.div 
              variants={containerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="grid grid-cols-2 lg:grid-cols-4 gap-6"
            >
              {features.map((feature, index) => (
                <motion.div 
                  key={index} 
                  variants={fadeInUp}
                  whileHover={{ scale: 1.05 }}
                  className="flex flex-col items-center text-center"
                >
                  <motion.div 
                    className="w-12 h-12 rounded-lg bg-charcoal/5 flex items-center justify-center mb-3"
                    whileHover={{ rotate: 10 }}
                  >
                    <feature.icon className="w-6 h-6 text-charcoal" />
                  </motion.div>
                  <p className="font-medium text-charcoal text-sm mb-1">{feature.title}</p>
                  <p className="text-charcoal/50 text-xs">{feature.desc}</p>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Process Section */}
      <section className="w-full bg-white py-12 lg:py-20">
        <div className="px-6 lg:px-[7vw]">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="font-display font-bold text-charcoal text-2xl lg:text-3xl mb-3">
              Our Process
            </h2>
            <p className="text-charcoal/60 max-w-2xl mx-auto">
              A proven six-step approach to deliver your storage solution on time and on budget.
            </p>
          </motion.div>

          <motion.div 
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-50px" }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            {processSteps.map((step, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                whileHover={{ y: -5, transition: { duration: 0.2 } }}
                className="bg-offwhite rounded-xl p-6 relative overflow-hidden"
              >
                <motion.span 
                  className="font-mono text-5xl font-bold text-charcoal/5 absolute top-4 right-4 select-none"
                  initial={{ scale: 0 }}
                  whileInView={{ scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ type: "spring", stiffness: 200, delay: index * 0.1 }}
                >
                  {step.step}
                </motion.span>
                <div className="relative z-10">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="font-mono bg-lime text-sm font-bold text-black p-1">{step.step}</span>
                    <h4 className="font-display font-bold text-charcoal text-lg">
                      {step.title}
                    </h4>
                  </div>
                  <p className="text-charcoal/60 text-sm">
                    {step.desc}
                  </p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="w-full bg-charcoal py-12 lg:py-16 overflow-hidden">
        <div className="px-6 lg:px-[7vw] text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="font-display font-bold text-white text-2xl lg:text-3xl mb-4">
              Ready to optimize your space?
            </h2>
            <p className="text-white/60 max-w-xl mx-auto mb-6">
              Get a free site survey and quote. We'll help you find the perfect storage solution for your business.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Link
                  to="/contact"
                  className="bg-lime text-charcoal px-8 py-4 rounded-lg font-medium inline-flex items-center justify-center gap-2 hover:bg-lime/90 transition-colors"
                >
                  Request a Quote
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </motion.div>
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <a 
                  href="/B-BBEEMolomoNR.pdf" 
                  download 
                  className="bg-white/10 text-white px-8 py-4 rounded-lg font-medium inline-flex items-center justify-center gap-2 hover:bg-white/20 transition-colors"
                >
                  <Download className="w-4 h-4" />
                  Download B-BBEE Certificate
                </a>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Capabilities;
